import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { reviewAcceptAssessment, reviewRejectAssessment } from '../../store/assessmentSingle/actions';
import { ReviewAcceptModal } from '../ReviewAcceptModal';
import { ReviewRejectModal } from '../ReviewRejectModal';
import { getUserStoredData } from '../../services';
import { IAppState } from 'store';
import './ReviewButton.scss';

interface IProps {
  assessmentId: number;
  reviewableAssessment?: boolean;
}

const ReviewButton = ({ assessmentId, reviewableAssessment }: IProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);

  const userRoleName = useSelector(
    (state: IAppState) => state.auth.profile?.UserRoleName,
  );

  const { roles } = getUserStoredData();

  let isFarmer = false;

  if (userRoleName) {
    isFarmer = userRoleName.toLowerCase().includes('farmer');
  } else if (roles) {
    try {
      const parsedRoles = JSON.parse(roles);

      if (Array.isArray(parsedRoles)) {
        isFarmer = parsedRoles.some(
          (role: string) => role && role.toLowerCase().includes('farmer'),
        );
      }
    } catch (e) {
      // ignore parsing errors and treat as no farmer role
      // console.error('Error parsing user roles', e);
    }
  }

  const handleAcceptClick = () => {
    setShowAcceptModal(true);
  };

  const handleRejectClick = () => {
    setShowRejectModal(true);
  };

  const handleAcceptConfirm = async () => {
    try {
      await dispatch(reviewAcceptAssessment(assessmentId));
      setShowAcceptModal(false);
      setIsReviewed(true);
    } catch (error) {
      console.error('Error accepting assessment:', error);
    }
  };

  const handleRejectConfirm = async (comment: string) => {
    try {
      await dispatch(reviewRejectAssessment(assessmentId, comment));
      setShowRejectModal(false);
      setIsReviewed(true);
    } catch (error) {
      console.error('Error rejecting assessment:', error);
    }
  };

  const handleAcceptExit = () => {
    setShowAcceptModal(false);
  };

  const handleRejectExit = () => {
    setShowRejectModal(false);
  };

  if (!reviewableAssessment || isFarmer || isReviewed) {
    return null;
  }

  return (
    <>
      <div className="review-buttons-container">
        <button
          className="btn action-primary review-accept-btn"
          onClick={handleAcceptClick}
        >
          {t('review_button_accept')}
        </button>
        <button
          className="btn action-secondary review-reject-btn"
          onClick={handleRejectClick}
        >
          {t('review_button_reject')}
        </button>
      </div>

      <ReviewAcceptModal
        isOpen={showAcceptModal}
        onAccept={handleAcceptConfirm}
        onExit={handleAcceptExit}
      />

      <ReviewRejectModal
        isOpen={showRejectModal}
        onReject={handleRejectConfirm}
        onExit={handleRejectExit}
      />
    </>
  );
};

export { ReviewButton };
